import React, { useState } from 'react';
import { ArrowLeft, Bot, Building2, FileText, Clock, Shield, Sparkles } from 'lucide-react';
import { AIAssistant } from './AIAssistant';
import { FoundationData } from '../../types/Foundation';
import { DocumentPreview } from './DocumentPreview';
import { FoundationForm } from './FoundationForm';

interface VirtualBuilderProps {
  onBack: () => void;
}

export const VirtualBuilder: React.FC<VirtualBuilderProps> = ({ onBack }) => {
  const [step, setStep] = useState(1);
  const [showAI, setShowAI] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [data, setData] = useState<FoundationData>({
    basicInfo: {
      name: '',
      purpose: '',
      type: '',
      startingCapital: '',
      location: ''
    },
    governance: {
      boardMembers: [],
      statutes: '',
      decisionMaking: ''
    },
    financial: {
      accountingPeriod: '',
      auditRequirements: '',
      investmentPolicy: ''
    },
    legal: {
      registrationDetails: '',
      taxStatus: '',
      complianceRequirements: ''
    }
  });

  const totalSteps = 4;

  const handleAIHelp = () => {
    setShowAI(true);
    setShowPreview(false);
  };

  const handleUpdateData = (section: keyof FoundationData, newData: any) => {
    setData(prev => ({
      ...prev,
      [section]: { ...prev[section], ...newData }
    }));
  };

  const handleImproveText = (text: string, field: string) => {
    // In a real implementation, this would call an AI service
    console.log(`Improving text for ${field}:`, text);
  };

  const handleNextStep = () => {
    if (step < totalSteps) {
      setStep(prev => prev + 1);
    } else {
      setShowPreview(true);
    }
  };

  const handlePrevStep = () => {
    if (step > 1) {
      setStep(prev => prev - 1);
      setShowPreview(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          Tillbaka
        </button>
        <button
          onClick={handleAIHelp}
          className="flex items-center gap-2 text-[#A3C1DA] hover:text-[#F2C1D1] transition-colors"
        >
          <Bot className="w-5 h-5" />
          AI-Hjälp
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="md:col-span-2">
          {showAI ? (
            <AIAssistant
              step={step}
              totalSteps={totalSteps}
              data={data}
              onImproveText={handleImproveText}
            />
          ) : showPreview ? (
            <DocumentPreview
              data={data}
              onBack={handlePrevStep}
              onDownload={() => console.log('Downloading document...')}
            />
          ) : (
            <FoundationForm
              step={step}
              totalSteps={totalSteps}
              data={data}
              onUpdate={handleUpdateData}
              onAskAI={handleAIHelp}
              onImproveText={handleImproveText}
            />
          )}
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-xl p-6 shadow-lg">
            <h3 className="text-lg font-semibold mb-4">Steg {step} av {totalSteps}</h3>
            <div className="space-y-4">
              {[
                { icon: <Building2 className="w-5 h-5" />, text: "Grundläggande information" },
                { icon: <FileText className="w-5 h-5" />, text: "Styrelse och förvaltning" },
                { icon: <Clock className="w-5 h-5" />, text: "Ekonomi och revision" },
                { icon: <Shield className="w-5 h-5" />, text: "Juridiska aspekter" }
              ].map((item, index) => (
                <div
                  key={index}
                  className={`flex items-center gap-3 p-3 rounded-lg ${
                    step === index + 1
                      ? 'bg-rose-50 text-rose-600'
                      : 'text-gray-500'
                  }`}
                >
                  {item.icon}
                  <span className="text-sm font-medium">{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gradient-to-br from-rose-50 to-white rounded-xl p-6 shadow-lg">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-5 h-5 text-rose-600" />
              <h3 className="font-semibold">AI-Assistans</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Låt vår AI hjälpa dig optimera din stiftelse
            </p>
            <button
              onClick={handleAIHelp}
              className="w-full bg-rose-600 text-white rounded-lg py-2 px-4 
                       hover:bg-rose-700 transition-colors flex items-center 
                       justify-center gap-2"
            >
              <Bot className="w-5 h-5" />
              Få AI-hjälp
            </button>
          </div>

          {!showAI && !showPreview && (
            <div className="flex justify-between">
              <button
                onClick={handlePrevStep}
                disabled={step === 1}
                className={`px-4 py-2 rounded-lg ${
                  step === 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Föregående
              </button>
              <button
                onClick={handleNextStep}
                className="px-4 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 
                         transition-colors"
              >
                {step === totalSteps ? 'Förhandsgranska' : 'Nästa'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};